import { AsyncSelect } from "components/AsyncInput/AsyncSelect";
import { showErrorToast } from "components/CustomAlerts/CustomToast";
import { Pagination } from "components/Pagination/Pagination";
import { DEFAULT_LIMIT_PAGINATION } from "constants/appConstants";
import { useFormik } from "formik";
import { CombinatoriaI } from "interfaces/Combinatoria";
import { ModalidadI } from "interfaces/Modalidad";
import { NivelI } from "interfaces/Nivel";
import { OpcionPedagogicaI } from "interfaces/OpcionPedagogica";
import { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PRIVATE_ROUTE } from "routes/privateRoutes";
import { getCombinatorias } from "services/combinatoria";
import { getModalidades } from "services/modalidad";
import { getNiveles, getNivelesPaginado } from "services/niveles";
import { getOpcionPedagogica } from "services/opcionPedagogica";
import { fetchAlcances } from "store/slices/appSlice";
import { AppDispatch, RootState } from "store/store";
import { CombinatoriaRowActions } from "./CombinatoriaTable/CombinatoriaRowActions";
import { CombinatoriaTable } from "./CombinatoriaTable/CombinatoriaTable";
import { get } from "http";

export const Combinatoria = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [elementos, setElementos] = useState<CombinatoriaI[]>();
  const [cantidad, setCantidad] = useState<number>();
  const [page, setPage] = useState<number>();
  const [limit, setLimit] = useState<number>();
  const [formFiltersKey, setformFiltersKey] = useState<number>(0);

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { alcances, loadingAppData } = useSelector(
    (st: RootState) => st.appData,
  );

  const initialValues = {
    modalidad_id: "",
    nivel_id: "",
    opcion_pedagogica_id: "",
  };

  const { values, handleChange, resetForm } = useFormik({
    initialValues,
    onSubmit: () => {},
  });

  const searchCombinatoria = async (params?: {
    filtros?: { [key: string]: any };
  }) => {
    try {
      setIsLoading(true);
      const response = await getCombinatorias({ ...params?.filtros });
      setElementos(response.elementos);
      setCantidad(response.cantidad);
      setPage(response.page);
      setLimit(params?.filtros?.limit ?? DEFAULT_LIMIT_PAGINATION);
    } catch (err) {
      showErrorToast("No fue posible cargar las combinatorias");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (elemento: CombinatoriaI) => {
    navigate(PRIVATE_ROUTE.COMBINATORIA_EDITAR + "/" + elemento.id);
  };

  const handleCreate = () => {
    navigate(PRIVATE_ROUTE.COMBINATORIA_CREAR);
  };

  const handleChangePagination = (newFiltros: {
    [key: string]: string | number;
  }) => {
    searchCombinatoria({ filtros: { ...values, ...newFiltros } });
  };

  const handleClearFilters = () => {
    resetForm();
    setformFiltersKey(formFiltersKey + 1);
    searchCombinatoria();
  };

  const handleChangeWithResetPage = (pageOne: any) => {
    setPage(1);
    handleChange(pageOne);
  };

  const refreshTable = () => {
    setPage(1);
    searchCombinatoria({ filtros: { ...values, page: 1 } });
  };

  const renderActions = (elemento: CombinatoriaI) => {
    return (
      <CombinatoriaRowActions
        elemento={elemento}
        onClickEdit={handleEdit}
        refreshTable={refreshTable}
      />
    );
  };

  useEffect(() => {
    searchCombinatoria({ filtros: { ...values, limit, page } });
  }, [values]);

  useEffect(() => {
    if (!alcances) dispatch(fetchAlcances());
  }, []);

  return (
    <section>
      <h1 className="section-title">Combinatoria</h1>

      <h2 className="section-subtitle mt-2">
        Desde acá podrás gestionar las combinatorias.
      </h2>

      <div className="d-flex justify-content-between mt-4 mb-3">
        <div
          key={`formFiltersKey=${formFiltersKey}`}
          className="d-flex column-gap-3"
        >
          <AsyncSelect
            onChange={handleChange}
            id="modalidad_id"
            values={values}
            placeholder={"Modalidad"}
            size="md"
            getter={getModalidades}
            paramKey={"nombre"}
            variant="white-container"
            arrElementsPath="elementos"
            width="200px"
            optionsFormatConfig={{
              value: "id",
              label: (el: ModalidadI) => `${el.nombre}`,
            }}
            required
            noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
            emptySelectMessage={`Ingresar un nombre`}
          />
          <AsyncSelect
            onChange={handleChange}
            id="nivel_id"
            values={values}
            placeholder={"Nivel"}
            size="md"
            getter={getNiveles}
            paramKey={"nombre"}
            variant="white-container"
            arrElementsPath="elementos"
            width="200px"
            optionsFormatConfig={{
              value: "id",
              label: (el: NivelI) => `${el.nombre}`,
            }}
            required
            noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
            emptySelectMessage={`Ingresar un nombre`}
          />
          <AsyncSelect
            onChange={handleChange}
            id="opcion_pedagogica_id"
            values={values}
            placeholder={"Opción pedagógica"}
            size="md"
            getter={getOpcionPedagogica}
            paramKey={"nombre"}
            variant="white-container"
            arrElementsPath="elementos"
            width="250px"
            optionsFormatConfig={{
              value: "id",
              label: (el: OpcionPedagogicaI) => `${el.nombre}`,
            }}
            required
            noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
            emptySelectMessage={`Ingresar un nombre`}
          />
          <Button
            size="sm"
            variant="secondary"
            className="clear-filters-button"
            onClick={handleClearFilters}
          >
            Limpiar filtros
          </Button>
        </div>
        <Button size="lg" onClick={handleCreate}>
          Nueva Combinatoria
        </Button>
      </div>

      {isLoading && (
        <div className="d-flex justify-content-center w-full">
          <Spinner />
        </div>
      )}

      {!isLoading && elementos && cantidad != undefined && (
        <>
          <CombinatoriaTable
            elementos={elementos}
            renderActions={renderActions}
          />
          <Pagination
            totalItems={cantidad}
            currentPage={page}
            currentLimit={limit}
            onChangePage={handleChangePagination}
            showPaginationOnlyOnMoreThanPages={1}
          />
        </>
      )}
    </section>
  );
};
